import { FaroDialogProps, FeedbackDialog } from "@faro-lotv/flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useFeedback } from "@hooks/feedback/use-feedback";
import { contactSupport } from "@utils/email-utils";
import { ContactSupportLocations } from "@custom-types/types";
import { runtimeConfig } from "@src/runtime-config";
import { FeedbackModalEvents } from "@utils/track-event/track-event-list";

/** The type of feedback dialog */
export type FeedbackType = "emotion" | "numbered";

interface Props extends Pick<FaroDialogProps,
  "onCancel" | "confirmText" | "cancelText" | "showXButton"
 > {
  /**
  * Type of feedback dialog
  * @default: "emotion"
 */
  type?: FeedbackType;

  /** Whether the dialog is open */
  isDialogOpen: boolean;

  /** Callback to close the dialog */
  onClose: () => void;

  /**
   * Optional title for the feedback dialog
   *
   * @default "How was your experience with the dashboard?"
   */
  title?: string;

  /**
   * Optional text for the feedback question
   *
   * @default "Want to tell us more?"
   */
  feedbackQuestionText?: string;

  /**
   * Optional name of the feature that the feedback is about.
   *
   * @default "General"
   */
  featureName?: string;

  /** Optional function to execute when the user submits feedback */
  onSubmitFeedback?: () => void;
}

/**
 * SendFeedbackDialog is a modal component that allows users to provide feedback on their experience with the dashboard.
 */
export function SendFeedbackDialog({
  type = "emotion",
  isDialogOpen,
  onClose,
  title,
  featureName = "General",
  feedbackQuestionText,
  onSubmitFeedback,
  onCancel,
  confirmText,
  cancelText,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  showXButton,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { sendFeedback } = useFeedback();

  async function handleFeedbackSubmit(rating: number, feedbackText: string): Promise<void> {
    await sendFeedback({
      featureName,
      feedbackMessage: feedbackText,
      selectedRating: rating,
    });
    onClose();
    onSubmitFeedback?.();
  }

  function handleContactSupportClick(): void {
    contactSupport({
      location: ContactSupportLocations.feedbackDialog,
      trackEvent,
    });
    onClose();
  }

  function handelCancelClick(): void {
    trackEvent({
      name: FeedbackModalEvents.cancelFeedback,
    });
    onCancel?.();
    onClose();
  }

  function handelCloseClick(): void {
    // Track the event when the dialog is closed with X button
    trackEvent({
      name: FeedbackModalEvents.closeFeedbackDialog,
    });
    onClose();
  };

  return (
    <FeedbackDialog
      type={type}
      title={title}
      isDialogOpen={isDialogOpen}
      feedbackQuestionText={feedbackQuestionText}
      supportMailAddress={runtimeConfig.mailSupportAddress}
      onContactSupportClick={handleContactSupportClick}
      onSubmitFeedback={handleFeedbackSubmit}
      onCancel={handelCancelClick}
      onClose={handelCloseClick}
      confirmText={confirmText}
      cancelText={cancelText}
      showXButton={showXButton}
    />
  );
}
